import AnimateObject from 'epic-fortnite-react/components/common/AnimateObject';
import epicTracking from 'epic-tracking';
import React from 'react';
import VisibilitySensor from 'react-visibility-sensor';

import {FeatureVideo} from '../FeatureHeroCarousel/Types';

import {
  Content,
  CrewContentContainer,
  CrewSectionContainer,
  CrewVBucksLogoContainer,
  VBucksBody,
  VBucksImage,
  VBucksTitle,
} from './components';
import CrewImage from './components/CrewImage';
import CrewBackground from './CrewBackground';
import CrewCTA from './CrewCTA';
import CrewVBucksImage from './CrewVBucksImage';

interface Cta {
  isCtaLinkExternal: boolean;
  textOnly: boolean;
  ctaString: string;
  disabled: boolean;
}
interface CmsData {
  title: string;
  background: FeatureVideo;
  battleStarAlt: string;
  battleStarLogo: string;
  crewLogo: string;
  crewLogoAlt: string;
  crewPattern: string;
  cta: Cta;
  description: string;
  vbucksAlt: string;
  vbucksImage: string;
  crewImg: string;
  crewImgMobile: string;
}

interface Props {
  data: CmsData;
  mobile: boolean;
  tablet: boolean;
}
const CrewSection = ({data, mobile, tablet}: Props) => {
  const {
    background,
    crewLogo,
    crewLogoAlt,
    crewPattern,
    battleStarLogo,
    battleStarAlt,
    vbucksAlt,
    vbucksImage,
    title,
    description,
    cta,
    crewImg,
    crewImgMobile,
  } = data;

  const [visible, setVisibility] = React.useState(false);

  const setSectionVisibility = React.useCallback(
    (visibility) => {
      setVisibility(visibility);
      if (visibility) {
        epicTracking.trackEvent('Interaction', {
          interactionType: 'scrollViewSection',
          eventCategory: 'BattlePass Crew Section',
          eventAction: 'CrewSection-inView',
        });
      }
    },
    [setVisibility]
  );

  return (
    <VisibilitySensor
      active={!visible}
      onChange={setSectionVisibility}
      partialVisibility
      minTopValue={0}>
      <CrewSectionContainer>
        {background && (
          <CrewBackground
            crewPattern={crewPattern}
            background={background}
            mobile={mobile}
            tablet={tablet}
          />
        )}
        <CrewContentContainer>
          {crewImg && (
            <AnimateObject
              className={'crew-image'}
              minTop={20}
              speed={1.5}
              from={{x: `-=50`, autoAlpha: 0}}
              to={{x: 0, autoAlpha: 1, delay: 0.3}}>
              <CrewImage mobile={crewImgMobile} tablet={crewImgMobile} desktop={crewImg} />
            </AnimateObject>
          )}
        </CrewContentContainer>
        <CrewContentContainer>
          <Content>
            {crewLogo && (
              <CrewVBucksLogoContainer>
                <AnimateObject
                  className={'crew-logo'}
                  minTop={20}
                  speed={1.5}
                  from={{x: `-=50`, autoAlpha: 0}}
                  to={{x: 0, autoAlpha: 1, delay: 0.3}}>
                  <VBucksImage src={crewLogo} alt={crewLogoAlt} />
                </AnimateObject>
              </CrewVBucksLogoContainer>
            )}
            {title && (
              <AnimateObject
                minTop={20}
                speed={1.5}
                from={{y: `+=50`, autoAlpha: 0}}
                to={{y: 0, autoAlpha: 1, delay: 0.3}}>
                <VBucksTitle>{title}</VBucksTitle>
              </AnimateObject>
            )}

            {description && (
              <AnimateObject
                minTop={20}
                speed={1.5}
                from={{y: `+=50`, autoAlpha: 0}}
                to={{y: 0, autoAlpha: 1, delay: 0.4}}>
                <VBucksBody>{description}</VBucksBody>
              </AnimateObject>
            )}
            {cta && <CrewCTA cta={cta} />}
          </Content>
        </CrewContentContainer>
        {(vbucksImage || battleStarLogo) && (
          <CrewVBucksImage
            vbucksImage={vbucksImage}
            vbucksAlt={vbucksAlt}
            battleStarLogo={battleStarLogo}
            battleStarAlt={battleStarAlt}
          />
        )}
      </CrewSectionContainer>
    </VisibilitySensor>
  );
};

export default CrewSection;
